import type { MaterialCertificate } from '../features/locations/types';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface AttachCertificateRequest {
  file: File;
  /** Every bin the certificate covers; the server writes one per bin, for the material it holds. */
  binIds: string[];
  description: string;
  reference: string;
  /** The date on the certificate, YYYY-MM-DD. */
  issuedAt: string;
}

async function failure(response: Response, fallback: string): Promise<Error> {
  try {
    const body = await response.json();
    return new Error(body?.error || body?.details || fallback);
  } catch {
    return new Error(fallback);
  }
}

export const materialCertificateService = {
  /** Every certificate on every bin at a site, newest date first. */
  listForSite: async (siteId: string): Promise<MaterialCertificate[]> => {
    const response = await fetch(`${API_URL}/material-certificates?siteId=${encodeURIComponent(siteId)}`, {
      credentials: 'include',
    });
    if (!response.ok) throw await failure(response, 'Failed to load certificates');
    const body = (await response.json()) as { certificates: MaterialCertificate[] };
    return body.certificates ?? [];
  },

  /**
   * Where the browser opens the file. The server streams it from private storage, so this is
   * the only link to hand out; the stored URL never reaches the client.
   */
  fileUrl: (id: string): string => `${API_URL}/material-certificates/${encodeURIComponent(id)}/file`,

  attach: async (request: AttachCertificateRequest): Promise<MaterialCertificate[]> => {
    const form = new FormData();
    form.append('file', request.file);
    for (const id of request.binIds) form.append('binIds', id);
    form.append('description', request.description);
    form.append('reference', request.reference);
    form.append('issuedAt', request.issuedAt);

    const response = await fetch(`${API_URL}/material-certificates`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) throw await failure(response, 'Failed to attach the certificate');
    const body = (await response.json()) as { certificates: MaterialCertificate[] };
    return body.certificates ?? [];
  },

  remove: async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/material-certificates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) throw await failure(response, 'Failed to remove the certificate');
  },
};
